import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { usePage, useHomepage } from "@/hooks/useProducts";
import DocumentHead from "@/components/DocumentHead";
import QuickEditWrapper from "@/components/quickedit/QuickEditWrapper";
import TenantMarketingLayout from "@/components/tenant-marketing/TenantMarketingLayout";
import { SITE_NAME } from "@/config/site";

interface TenantMarketingPageProps {
  /** Slug of the `pages` row to render (e.g., "features", "pricing", "customers", "about"). */
  slug: string;
  /** Heading shown in the not-found empty state if the page row is missing. */
  fallbackTitle: string;
  /** One-line description used as fallback subtitle + SEO description. */
  fallbackDescription?: string;
}

/**
 * Renders a marketing page from the `pages` table. Used by the SaaS site
 * routes (/features, /pricing, /case-studies, /about) when the tenant is NOT
 * rumio.world — so every non-rumio SaaS tenant gets its own tenant-specific
 * content instead of Rumio's hardcoded React components.
 *
 * Loading + not-found UI mirrors DynamicPage for visual consistency.
 */
export default function TenantMarketingPage({
  slug,
  fallbackTitle,
  fallbackDescription,
}: TenantMarketingPageProps) {
  const { data: page, isLoading, isError } = usePage(slug);
  // Structured layouts (pricing especially) reuse the tenant's already-seeded
  // homepage_sections (pricing_tiers / faq / cta_banner). Fetched here so the
  // hook order stays stable across the loading/error early returns below.
  const { data: homepage } = useHomepage();
  const sections = homepage?.sections ?? [];

  if (isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8" aria-busy="true">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-surface-muted" />
          <div className="h-10 rounded bg-surface-muted w-3/4" />
          <div className="space-y-3">
            <div className="h-4 rounded bg-surface-muted" />
            <div className="h-4 rounded bg-surface-muted w-2/3" />
            <div className="h-4 rounded bg-surface-muted w-1/2" />
          </div>
        </div>
      </main>
    );
  }

  if (isError || !page) {
    // Tenant-generic empty state — not Rumio-branded.
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 text-center">
        <DocumentHead title={`${fallbackTitle} — ${SITE_NAME}`} description={fallbackDescription} />
        <FileText className="mx-auto h-16 w-16 text-ink-muted" aria-hidden="true" />
        <h1 className="mt-4 text-2xl font-bold text-ink">{fallbackTitle}</h1>
        <p className="mt-2 text-ink-soft">
          This page hasn't been set up yet. Admins can edit it at{" "}
          <Link to="/admin/pages" className="text-sage-700 underline">
            /admin/pages
          </Link>
          .
        </p>
      </main>
    );
  }

  return (
    <QuickEditWrapper href={`/admin/pages/${(page as any).id}`} label={`Edit page: ${(page as any).title}`}>
      <DocumentHead
        title={`${(page as any).title} — ${SITE_NAME}`}
        description={(page as any).meta_description || fallbackDescription}
        canonicalUrl={`/${slug === "customers" ? "case-studies" : slug}`}
      />
      <TenantMarketingLayout slug={slug} page={page as any} sections={sections} siteName={SITE_NAME} />
    </QuickEditWrapper>
  );
}
